import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle open animation
  useEffect(() => {
    if (menuOpen) {
      setMenuVisible(true);
    } else {
      // delay unmount to allow close animation
      const timer = setTimeout(() => setMenuVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [menuOpen]);

  const navLinks = [
    { label: 'الرئيسية', path: '/' },
    { label: 'استكشاف الحملات', path: '/explore' },
    { label: 'ابدأ حملة', path: '/create' },
    { label: 'من نحن', path: '/about' },
    { label: 'تواصل معنا', path: '/contact' },
    { label: 'لوحة التحكم', path: '/dashboard' },
  ];

  const isTransparent = isHome && !scrolled;

  return (
    <nav
      dir="rtl"
      className="fixed top-0 right-0 left-0 z-50 transition-all duration-300"
      style={{
        background: isTransparent && !menuOpen ? 'transparent' : 'rgba(255,255,255,0.92)',
        backdropFilter: isTransparent && !menuOpen ? 'none' : 'blur(12px)',
        borderBottom: isTransparent && !menuOpen ? 'none' : '1px solid rgba(231,229,228,0.6)',
        boxShadow: isTransparent && !menuOpen ? 'none' : '0 1px 8px rgba(0,0,0,0.04)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-20 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex flex-col items-center cursor-pointer gap-0">
            <img
              src="https://storage.readdy-site.link/project_files/bd0a3593-735c-4714-ba50-f8e60929140a/378ea22b-7394-471a-a9d9-995de69b5021_heba11.png"
              alt="هِبة"
              className={`h-14 md:h-20 w-auto object-contain transition-all duration-300 ${
                isTransparent && !menuOpen ? 'brightness-0 invert' : ''
              }`}
            />
            <span
              className={`text-[10px] md:text-xs font-light transition-colors whitespace-nowrap -mt-3 md:-mt-5 ${
                isTransparent && !menuOpen ? 'text-white/80 drop-shadow-sm' : 'text-emerald-600/80'
              }`}
              style={{ fontFamily: "'Noto Naskh Arabic', serif" }}
            >
              منصة هِبة لدعم الشباب
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isTransparent
                    ? 'text-white/90 hover:text-white'
                    : location.pathname === link.path
                    ? 'text-emerald-600'
                    : 'text-stone-700 hover:text-emerald-600'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className={`text-sm font-medium px-4 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? 'text-white border border-white/40 hover:bg-white/10'
                  : 'text-stone-700 hover:text-emerald-600'
              }`}
            >
              تسجيل الدخول
            </Link>
            <Link
              to="/create"
              className="text-sm font-semibold px-5 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              ابدأ حملتك
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
              isTransparent && !menuOpen ? 'text-white' : 'text-stone-700'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i
              className={`text-xl transition-transform duration-300 ${
                menuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line rotate-0'
              }`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu with animation */}
      {menuVisible && (
        <div
          ref={menuRef}
          className={`md:hidden border-t border-stone-100/60 px-4 py-4 flex flex-col gap-1 transition-all duration-300 ease-in-out ${
            menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
          }`}
          style={{ background: 'transparent' }}
        >
          {navLinks.map((link, index) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className={`text-stone-700 font-medium py-3 px-3 rounded-xl border-b border-stone-100/60 cursor-pointer transition-all duration-200 hover:bg-emerald-50 hover:text-emerald-700 ${
                location.pathname === link.path ? 'text-emerald-600 bg-emerald-50/60' : ''
              }`}
              style={{
                transitionDelay: menuOpen ? `${index * 40}ms` : '0ms',
                transform: menuOpen ? 'translateX(0)' : 'translateX(10px)',
                opacity: menuOpen ? 1 : 0,
              }}
            >
              {link.label}
            </Link>
          ))}
          <div
            className="flex gap-3 pt-3"
            style={{
              transitionDelay: menuOpen ? `${navLinks.length * 40}ms` : '0ms',
              opacity: menuOpen ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          >
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="flex-1 text-center text-sm font-medium py-2.5 rounded-full border border-stone-200 text-stone-700 hover:border-emerald-300 hover:text-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              تسجيل الدخول
            </Link>
            <Link
              to="/create"
              onClick={() => setMenuOpen(false)}
              className="flex-1 text-center text-sm font-semibold py-2.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              ابدأ حملتك
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
